import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import useSubscriptionModal from "@hooks/use_subscription_modal.ts";
import { EmailSchedulesContext } from "@components/preferences";
import type { EmailSchedule } from "@components/job_alerts/types.ts";
import { JobAlertsHeader } from "@components/job_alerts/header.tsx";
import { ErrorState } from "@components/job_alerts/error_state.tsx";
import { LoadingState } from "@components/job_alerts/loading_state.tsx";
import { JobAlertsGrid } from "@components/job_alerts/grid.tsx";
import { PausedBanner } from "@components/job_alerts/paused_banner.tsx";
import { EnableAlertsModal } from "@components/job_alerts/enable_alerts_modal.tsx";

export type JobAlertsPanelProps = {
  paths: {
    notificationSchedulePath: string;
    emailPreferencesPath: string;
  };
  emailSchedule: EmailSchedule;
  emailSchedules: EmailSchedule[];
  jobAlertsPaused: boolean;
};

export const JobAlertsPanel = ({
  paths,
  emailSchedule,
  emailSchedules,
  jobAlertsPaused,
}: JobAlertsPanelProps) => {
  const [showPausedModal, setShowPausedModal] = useState(false);
  const [pendingEditPath, setPendingEditPath] = useState<string | null>(null);
  const [pendingCompanyName, setPendingCompanyName] = useState<string>("");
  const { openModal } = useSubscriptionModal();

  const handleEditWhilePaused = (editPath: string, companyName: string) => {
    setPendingEditPath(editPath);
    setPendingCompanyName(companyName);
    setShowPausedModal(true);
  };

  const openEditModal = () => {
    setShowPausedModal(false);
    if (!pendingEditPath) return;

    openModal(pendingEditPath);
  };

  return (
    <>
      <EmailSchedulesContext.Provider
        value={{
          emailSchedule,
          emailSchedules,
          notificationSchedulePath: paths.notificationSchedulePath,
        }}
      >
        <ErrorBoundary fallback={<ErrorState />}>
          <Suspense fallback={<LoadingState />}>
            <JobAlertsHeader jobAlertsPaused={jobAlertsPaused} />
            {jobAlertsPaused && <PausedBanner emailPreferencesPath={paths.emailPreferencesPath} />}
            <JobAlertsGrid
              onEditWhilePaused={jobAlertsPaused ? handleEditWhilePaused : undefined}
            />
          </Suspense>
        </ErrorBoundary>
      </EmailSchedulesContext.Provider>
      <EnableAlertsModal
        open={showPausedModal}
        onOpenChange={setShowPausedModal}
        companyName={pendingCompanyName}
        emailPreferencesPath={paths.emailPreferencesPath}
        onDismiss={openEditModal}
      />
    </>
  );
};
